import {
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import axios from "lib/axios";

export type ServerResourceListMeta = {
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
};

export type ServerResourceListResult<TData> = {
  items: TData[];
  meta: ServerResourceListMeta;
};

export type UseServerResourceListProps<TData> = {
  resourceName: string;
  path?: string;
  params?: Record<string, string | number | boolean | undefined>;
  dataKey?: string;
  metaKey?: string;
} & Omit<
  UseQueryOptions<ServerResourceListResult<TData>>,
  "queryKey" | "queryFn"
>;

export const useServerResourceList = <TData = Record<string, unknown>>({
  resourceName,
  path,
  params = {},
  dataKey,
  metaKey = "meta",
  ...props
}: UseServerResourceListProps<TData>): UseQueryResult<
  ServerResourceListResult<TData>
> => {
  return useQuery({
    queryFn: async () => {
      const { data } = await axios.get(path ?? `${resourceName}`, { params });
      const items = (data[dataKey ?? resourceName] ?? []) as TData[];
      const responseMeta = data[metaKey] as Partial<ServerResourceListMeta> | undefined;

      return {
        items,
        meta: {
          total: responseMeta?.total ?? items.length,
          page: responseMeta?.page ?? 1,
          pageSize: responseMeta?.pageSize ?? items.length,
          totalPages: responseMeta?.totalPages ?? (items.length > 0 ? 1 : 0),
        },
      };
    },
    ...props,
    queryKey: [resourceName, "server-list", params],
  });
};
